import { useState } from 'react';
import round from '../../../helpers/round';
import CalculationErrorMessage from '../../common/CalculationErrorMessage';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultChecks = ({ result, isAllChecksOK }) => {
  const {
    fndWidth,
    fndLength,
    eccentricityX,
    eccentricityY,
    columnDimensionX,
    columnDimensionY,
    normal_v,
    slidingFactorOfSafety,
  } = result.input;

  const {
    canUserSeeCalculationSteps,
    eccentricityCondition,
    isEccentricityX_OK,
    isEccentricityY_OK,
    isInclinedLoading,
    minFndWidth,
    minFndLength,
    isMinFndWidth_OK,
    isMinFndlength_OK,
    phips,
    ca,
    effectiveFndArea,
    FMax,
    HMax,
    slidingStability,
    isSliding_OK,
  } = result.output;

  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const isEccentricityCheckOK = isEccentricityX_OK && isEccentricityY_OK;
  const isMinFndWidthCheckOK = isMinFndWidth_OK;
  const isMinFndLengthCheckOK = isMinFndlength_OK;
  const isSlidingCheckOK = isSliding_OK;
  const hasAnyCheckErrors =
    !isEccentricityX_OK || !isEccentricityY_OK || !isMinFndWidth_OK || !isMinFndlength_OK || !isSliding_OK;

  const [showEccentricityCheckDetails, setShowEccentricityCheckDetails] = useState(false);
  const [showMinFndWidthCheckDetails, setShowMinFndWidthCheckDetails] = useState(false);
  const [showMinFndLengthCheckDetails, setShowMinFndLengthCheckDetails] = useState(false);
  const [showSlidingCheckDetails, setShowSlidingCheckDetails] = useState(false);

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Initial Checks Before Calculations
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section introduces the preliminary Hansen checks that will be finalized next: eccentricity limits, minimum
          foundation width, minimum foundation length, and sliding stability. These checks will be used to confirm that the
          geometry and loading conditions are acceptable before detailed capacity comparisons.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Eccentiricty Checks */}
          <Alert
            icon={isSmallScreen ? false : isEccentricityCheckOK ? <CheckCircleSuccessIcon /> : <CancelIcon fontSize="small" />}
            severity={isEccentricityCheckOK ? 'success' : 'error'}
            variant="outlined"
            sx={{
              backgroundColor: isEccentricityCheckOK ? 'rgba(46,125,50,0.04)' : 'rgba(211,47,47,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowEccentricityCheckDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showEccentricityCheckDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>Eccentricity</b>
              {isEccentricityCheckOK ? (
                <>
                  <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />
                  <Typography variant="body2" color="success.main" fontWeight={600} component="span">
                    OK!
                  </Typography>
                </>
              ) : (
                <>
                  <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />
                  <Typography variant="body2" color="error.main" fontWeight={600} component="span">
                    Not OK!
                  </Typography>
                </>
              )}
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showEccentricityCheckDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  For the eccentricity check, both directions must satisfy e &le; B/6.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  e<sub>x</sub> = {round(eccentricityX, 2)} &le; {round(eccentricityCondition, 2)} = B/6{' '}
                  {isEccentricityX_OK ? (
                    <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                      (OK)
                    </Typography>
                  ) : (
                    <Typography component="span" variant="body2" color="error.main" fontWeight={600}>
                      (Not OK)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  e<sub>y</sub> = {round(eccentricityY, 2)} &le; {round(eccentricityCondition, 2)} = B/6{' '}
                  {isEccentricityY_OK ? (
                    <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                      (OK)
                    </Typography>
                  ) : (
                    <Typography component="span" variant="body2" color="error.main" fontWeight={600}>
                      (Not OK)
                    </Typography>
                  )}
                </Typography>
                {isEccentricityCheckOK ? (
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    Both eccentricity conditions are satisfied.
                  </Typography>
                ) : (
                  <Box sx={{ mt: 0.5 }}>
                    {!isEccentricityX_OK && (
                      <Typography variant="body2" fontWeight={600} color="error.main" sx={{ mb: 0.5 }}>
                        Eccentricity in x direction exceeds limits. Either increase foundation width or decrease eccentricity in x
                        direction.
                      </Typography>
                    )}
                  </Box>
                )}
              </Collapse>
            )}
          </Alert>
          {/* Min Fnd Width Check */}
          <Alert
            icon={isSmallScreen ? false : isMinFndWidthCheckOK ? <CheckCircleSuccessIcon /> : <CancelIcon fontSize="small" />}
            severity={isMinFndWidthCheckOK ? 'success' : 'error'}
            variant="outlined"
            sx={{
              backgroundColor: isMinFndWidthCheckOK ? 'rgba(46,125,50,0.04)' : 'rgba(211,47,47,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowMinFndWidthCheckDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showMinFndWidthCheckDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>Min. Foundation Width</b>
              {isMinFndWidthCheckOK ? (
                <>
                  <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />
                  <Typography variant="body2" color="success.main" fontWeight={600} component="span">
                    OK!
                  </Typography>
                </>
              ) : (
                <>
                  <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />
                  <Typography variant="body2" color="error.main" fontWeight={600} component="span">
                    Not OK!
                  </Typography>
                </>
              )}
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showMinFndWidthCheckDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Minimum foundation width is checked with B<sub>min</sub> = 4(e<sub>y</sub>) + w<sub>y</sub>, and the requirement
                  is B<sub>min</sub> &le; B.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  B<sub>min</sub> &nbsp;&#8804;&nbsp; B ?
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  B<sub>min</sub> = 4(e<sub>y</sub>)+w<sub>y</sub> &nbsp;&#8804;&nbsp; B
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  B<sub>min</sub> = 4({round(eccentricityY, 2)})+{round(columnDimensionY, 2)}&nbsp;&#8804;&nbsp;{' '}
                  {round(fndWidth, 2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  B<sub>min</sub> = {round(minFndWidth, 2)} &nbsp;&#8804;&nbsp; {round(fndWidth, 2)}{' '}
                  {isMinFndWidthCheckOK ? (
                    <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                      (&#10004;)
                    </Typography>
                  ) : (
                    <Typography component="span" variant="body2" color="error.main" fontWeight={600}>
                      (&#10006;)
                    </Typography>
                  )}
                </Typography>
              </Collapse>
            )}
          </Alert>
          {/* Min Fnd Length Check */}
          <Alert
            icon={isSmallScreen ? false : isMinFndLengthCheckOK ? <CheckCircleSuccessIcon /> : <CancelIcon fontSize="small" />}
            severity={isMinFndLengthCheckOK ? 'success' : 'error'}
            variant="outlined"
            sx={{
              backgroundColor: isMinFndLengthCheckOK ? 'rgba(46,125,50,0.04)' : 'rgba(211,47,47,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowMinFndLengthCheckDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showMinFndLengthCheckDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>Min. Foundation Length</b>
              {isMinFndLengthCheckOK ? (
                <>
                  <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />
                  <Typography variant="body2" color="success.main" fontWeight={600} component="span">
                    OK!
                  </Typography>
                </>
              ) : (
                <>
                  <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />
                  <Typography variant="body2" color="error.main" fontWeight={600} component="span">
                    Not OK!
                  </Typography>
                </>
              )}
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showMinFndLengthCheckDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Minimum foundation length is checked with L<sub>min</sub> = 4(e<sub>x</sub>) + w<sub>x</sub>, and the
                  requirement is L<sub>min</sub> &le; L.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  L<sub>min</sub> &nbsp;&#8804;&nbsp; L ?
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  L<sub>min</sub> = 4(e<sub>x</sub>)+w<sub>x</sub> &nbsp;&#8804;&nbsp; L
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  L<sub>min</sub> = 4({round(eccentricityX, 2)})+{round(columnDimensionX, 2)}&nbsp;&#8804;&nbsp;{' '}
                  {round(fndLength, 2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  L<sub>min</sub> = {round(minFndLength, 2)} &nbsp;&#8804;&nbsp; {round(fndLength, 2)}{' '}
                  {isMinFndLengthCheckOK ? (
                    <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                      (&#10004;)
                    </Typography>
                  ) : (
                    <Typography component="span" variant="body2" color="error.main" fontWeight={600}>
                      (&#10006;)
                    </Typography>
                  )}
                </Typography>
              </Collapse>
            )}
          </Alert>
          {/* Sliding Check */}
          {isInclinedLoading && (
            <Alert
              icon={isSmallScreen ? false : isSlidingCheckOK ? <CheckCircleSuccessIcon /> : <CancelIcon fontSize="small" />}
              severity={isSlidingCheckOK ? 'success' : 'error'}
              variant="outlined"
              sx={{
                backgroundColor: isSlidingCheckOK ? 'rgba(46,125,50,0.04)' : 'rgba(211,47,47,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSlidingCheckDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSlidingCheckDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Sliding Stability</b>
                {isSlidingCheckOK ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />
                    <Typography variant="body2" color="success.main" fontWeight={600} component="span">
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />
                    <Typography variant="body2" color="error.main" fontWeight={600} component="span">
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showSlidingCheckDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Sliding stability is checked by comparing available resistance against the required factor of safety.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    H<sub>max</sub> / Max (H<sub>B</sub>, H<sub>L</sub>) &ge; FS<sub>Sliding</sub> ?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    H<sub>max</sub> = V tan&phi; + C<sub>a</sub> A<sub>f</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    H<sub>max</sub> = ({round(normal_v, 2)}) (tan{round(phips, 2)}) + ({round(ca, 2)}) (
                    {round(effectiveFndArea, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    H<sub>max</sub> = {round(FMax, 0)} kPa
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    {round(FMax, 2)} / {round(HMax, 2)} &ge; {round(slidingFactorOfSafety, 2)} ?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {round(slidingStability, 2)} &ge; {round(slidingFactorOfSafety, 2)} ?{' '}
                    {isSlidingCheckOK ? (
                      <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                        (&#10004;)
                      </Typography>
                    ) : (
                      <Typography component="span" variant="body2" color="error.main" fontWeight={600}>
                        (&#10006;)
                      </Typography>
                    )}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
        {hasAnyCheckErrors && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 0.75 }}>
              Notes About Initial Checks
            </Typography>
            {!isEccentricityX_OK && (
              <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                <strong>Eccentricity:</strong> Eccentricity in x direction exceeds limits. Either increase foundation width or
                decrease eccentricity in x direction.
              </Typography>
            )}
            {!isEccentricityY_OK && (
              <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                <strong>Eccentricity:</strong> Eccentricity in y direction exceeds limits. Either increase foundation width or
                decrease eccentricity in y direction.
              </Typography>
            )}
            {!isMinFndWidth_OK && (
              <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                <strong>Min. Foundation Width:</strong> Foundation width is less than minimum foundation width. Either increase
                foundation width or decrease eccentricity in y direction or column dimension in y direction.
              </Typography>
            )}
            {!isMinFndlength_OK && (
              <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                <strong>Min. Foundation Length:</strong> Foundation length is less than minimum foundation length. Either increase
                foundation length or decrease eccentricity in x direction or column dimension in x direction.
              </Typography>
            )}
            {!isSliding_OK && (
              <Typography variant="body2" color="error.main">
                <strong>Sliding Stability:</strong> There is a problem with sliding stability. Either increase normal force,
                cohesion and foundation area or decrease horizontal force to reach desired factor of safety against sliding.
              </Typography>
            )}
          </Box>
        )}
      </Alert>
    </Box>
  );
};
export default HansenResultChecks;
